// src/utils/validationSchema.js
// -----------------------------------------------------------------------------
// Centralized global validation schema & functions driven by field configuration.
// Supports varchar/string max lengths, numeric ranges, date format, email, phone,
// and FK/select validation.
// -----------------------------------------------------------------------------

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_REGEX = /^[\+]?[0-9\s\-\(\)]{7,20}$/;

/**
 * Validates a single field value against its field definition config.
 * @param {Object} field - Field config object
 * @param {any} value - Field input value
 * @returns {string|null} - Error message string or null if valid
 */
export function validateField(field, value) {
  const {
    name,
    label = name,
    type = 'string',
    required = false,
    maxLength,
    min,
    max,
    pattern,
    options,
  } = field;

  // Trim string values for check
  const stringValue = value !== undefined && value !== null ? String(value).trim() : '';

  // 1. Required field check
  if (required && !stringValue) {
    if (field.requiredMessage) return field.requiredMessage;
    const typeLower = type ? type.toLowerCase() : '';
    if (typeLower === 'select' || typeLower === 'fk') {
      const cleanLabel = label.replace(/\s*\*+$/, '').trim();
      const vowel = /^[aeiou]/i.test(cleanLabel) ? 'an' : 'a';
      return `Please select ${vowel} ${cleanLabel.toLowerCase()}`;
    }
    return 'This field is required';
  }

  // If value is empty and not required, skip type validations
  if (!stringValue) {
    return null;
  }

  // 2. Type-based & custom validations
  switch (type.toLowerCase()) {
    case 'string':
    case 'varchar':
    case 'text':
      if (maxLength && stringValue.length > maxLength) {
        return `${label} cannot exceed ${maxLength} characters.`;
      }
      break;

    case 'int':
    case 'integer':
    case 'decimal':
    case 'number': {
      const num = Number(stringValue);
      if (isNaN(num)) {
        return `${label} must be a valid number.`;
      }
      if (type.toLowerCase() === 'int' || type.toLowerCase() === 'integer') {
        if (!Number.isInteger(num)) {
          return `${label} must be a valid integer.`;
        }
      }
      if (min !== undefined && num < min) {
        return `${label} must be at least ${min}.`;
      }
      if (max !== undefined && num > max) {
        return `${label} must be no greater than ${max}.`;
      }
      break;
    }

    case 'date':
    case 'datetime': {
      const dateTimestamp = Date.parse(stringValue);
      if (isNaN(dateTimestamp)) {
        return `${label} must be a valid date.`;
      }
      break;
    }

    case 'email':
      if (!EMAIL_REGEX.test(stringValue)) {
        return `Please enter a valid email address for ${label}.`;
      }
      if (maxLength && stringValue.length > maxLength) {
        return `${label} cannot exceed ${maxLength} characters.`;
      }
      break;

    case 'phone':
      if (!PHONE_REGEX.test(stringValue)) {
        return `Please enter a valid phone number for ${label}.`;
      }
      break;

    case 'select':
    case 'fk': {
      if (options && Array.isArray(options) && options.length > 0) {
        const isValidOption = options.some((opt) => String(opt.value) === stringValue);
        if (!isValidOption) {
          return `Please select a valid option for ${label}.`;
        }
      }
      break;
    }

    default:
      break;
  }

  // 3. Custom regex pattern check if specified
  if (pattern && pattern instanceof RegExp && !pattern.test(stringValue)) {
    return `${label} has an invalid format.`;
  }

  return null;
}

/**
 * Validates an entire form object against an array of field definitions.
 * @param {Array} fields - Array of field config objects
 * @param {Object} values - Current form values key-value map
 * @returns {Object} { errors: { [fieldName]: string }, isValid: boolean }
 */
export function validateForm(fields, values = {}) {
  const errors = {};

  fields.forEach((field) => {
    // Skip readOnly / primary key fields if auto-generated
    if (field.isPrimaryKey || field.readOnly) return;

    const val = values[field.name];
    const err = validateField(field, val);
    if (err) {
      errors[field.name] = err;
    }
  });

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
}

export default {
  validateField,
  validateForm,
  EMAIL_REGEX,
  PHONE_REGEX,
};
